import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { challengesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Trophy, Users, Rocket, Clock, CheckCircle, Lock, AlertTriangle, Loader2 } from 'lucide-react';

export const ChallengeVotePage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();

  const [challenge, setChallenge] = useState(null);
  const [startups, setStartups] = useState([]);
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null); // null = loading, string = message

  // Auth
  const [authenticated, setAuthenticated] = useState(false);
  const [judge, setJudge] = useState(null);
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [authHash, setAuthHash] = useState(searchParams.get('hash') || '');
  const [authLoading, setAuthLoading] = useState(false);

  // Vote
  const [selectedStartup, setSelectedStartup] = useState(null);
  const [votedStartup, setVotedStartup] = useState(null);
  const [alreadyVoted, setAlreadyVoted] = useState(false);
  const [voteSubmitting, setVoteSubmitting] = useState(false);
  const [voteConfirmed, setVoteConfirmed] = useState(false);
  const confirmVoteBtnRef = useRef(null);

  const handleSelectStartup = (id) => {
    setSelectedStartup(id);
    // Smooth-scroll to the confirm vote button on the next tick
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="confirm-vote-btn"]');
      btn?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  useEffect(() => {
    loadChallenge();
  }, [slug]);

  const loadChallenge = async () => {
    try {
      const res = await challengesAPI.getOne(slug);
      setChallenge(res.data);

      const [sRes, jRes] = await Promise.all([
        challengesAPI.getStartups(res.data.id),
        challengesAPI.getJudges(res.data.id),
      ]);
      setStartups(sRes.data);
      setJudges(jRes.data);

      // Check time window
      const now = new Date();
      const start = new Date(res.data.start_time);
      const end = new Date(res.data.end_time);
      if (now < start) {
        setBlocked(`La votazione non è ancora aperta. Inizio: ${start.toLocaleDateString('it-IT')} alle ${start.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`);
      } else if (now > end) {
        setBlocked(`La votazione è terminata il ${end.toLocaleDateString('it-IT')} alle ${end.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`);
      } else {
        setBlocked(null);
        // Auto-login if params present
        if (searchParams.get('hash') && searchParams.get('email')) {
          autoAuth(res.data.id);
        }
      }
    } catch (err) {
      toast.error('Challenge non trovata');
    } finally {
      setLoading(false);
    }
  };

  const autoAuth = async (challengeId) => {
    setAuthLoading(true);
    try {
      const res = await challengesAPI.authJudge({
        email: searchParams.get('email'),
        code: searchParams.get('code') || '',
        challenge_id: challengeId,
        auth_hash: searchParams.get('hash'),
      });
      if (res.data.authenticated) {
        setAuthenticated(true);
        setJudge(res.data.judge);
        setAuthHash(res.data.judge.auth_hash);
        if (res.data.already_voted) {
          setAlreadyVoted(true);
          setSelectedStartup(res.data.vote?.startup_id);
          if (res.data.vote?.startup) setVotedStartup(res.data.vote.startup);
        }
      }
    } catch (err) {
      // Auto-auth failed, user can try manually
    } finally {
      setAuthLoading(false);
    }
  };

  const handleManualAuth = async (e) => {
    e.preventDefault();
    if (!challenge) return;
    setAuthLoading(true);
    try {
      const payload = { email, challenge_id: challenge.id, auth_hash: authHash || undefined };
      if (code) payload.code = code;
      const res = await challengesAPI.authJudge(payload);
      if (res.data.authenticated) {
        setAuthenticated(true);
        setJudge(res.data.judge);
        setAuthHash(res.data.judge.auth_hash);
        if (res.data.already_voted) {
          setAlreadyVoted(true);
          setSelectedStartup(res.data.vote?.startup_id);
          if (res.data.vote?.startup) setVotedStartup(res.data.vote.startup);
        }
        toast.success('Autenticazione riuscita');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email non riconosciuta');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVote = async () => {
    if (!selectedStartup || !judge || !challenge) return;
    setVoteSubmitting(true);
    try {
      await challengesAPI.castVote({
        challenge_id: challenge.id,
        judge_id: judge.id,
        startup_id: selectedStartup,
        auth_hash: authHash,
      });
      setVoteConfirmed(true);
      setAlreadyVoted(true);
      toast.success('Voto registrato!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella votazione');
    } finally {
      setVoteSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!challenge) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Challenge non trovata</div>;

  // Blocked by time window
  if (blocked) {
    return (
      <div className="min-h-screen bg-sios-void flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-6">
          <Lock size={48} className="mx-auto text-muted-foreground" />
          <h1 className="font-heading text-2xl font-bold">{challenge.title}</h1>
          <p className="text-muted-foreground">{blocked}</p>
          <div className="glass rounded-xl p-6 space-y-3">
            <h3 className="font-medium flex items-center gap-2 justify-center"><Users size={16} className="text-primary" /> Giuria</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              {judges.map(j => (
                <div key={j.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 text-sm">
                  {j.image ? <img src={j.image.startsWith('http') ? j.image : `${process.env.REACT_APP_BACKEND_URL}${j.image}`} alt="" className="w-6 h-6 rounded-full object-cover" /> : null}
                  {j.first_name} {j.last_name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Auth form
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-sios-void flex items-center justify-center p-6">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <Trophy size={32} className="mx-auto text-primary mb-3" />
            <h1 className="font-heading text-2xl font-bold">{challenge.title}</h1>
            <p className="text-muted-foreground mt-1">Accedi per votare</p>
          </div>
          <form onSubmit={handleManualAuth} className="glass rounded-xl p-6 space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="La tua email" className="bg-white/5 border-white/10" data-testid="vote-email" />
              <p className="text-xs text-muted-foreground">Inserisci l'email con cui sei stato invitato come giurato.</p>
            </div>
            <Button type="submit" className="w-full" disabled={authLoading} data-testid="vote-auth-btn">
              {authLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Accedi alla Votazione
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Vote confirmed
  if (voteConfirmed || (alreadyVoted && !voteConfirmed)) {
    const voted = startups.find(s => s.id === selectedStartup) || votedStartup;
    return (
      <div className="min-h-screen bg-sios-void flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-6">
          <CheckCircle size={48} className="mx-auto text-green-400" />
          <h1 className="font-heading text-2xl font-bold">Grazie per il tuo voto!</h1>
          {voted ? (
            <>
              <p className="text-muted-foreground">Hai votato per:</p>
              <div className="glass rounded-xl p-6 flex items-center gap-4 justify-center">
                {voted.logo ? <img src={voted.logo.startsWith('http') ? voted.logo : `${process.env.REACT_APP_BACKEND_URL}${voted.logo}`} alt="" className="w-16 h-16 rounded-lg object-contain bg-white/10 p-2" /> : <Rocket size={32} className="text-primary" />}
                <span className="font-heading text-xl font-bold">{voted.name}</span>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">
              Hai già espresso il tuo voto per questa challenge.
              <br />
              <span className="text-xs">(La startup votata non è più presente nell'elenco)</span>
            </p>
          )}
        </div>
      </div>
    );
  }

  // Voting UI
  return (
    <div className="min-h-screen bg-sios-void p-6">
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center">
          <Trophy size={32} className="mx-auto text-primary mb-3" />
          <h1 className="font-heading text-2xl font-bold">{challenge.title}</h1>
          <p className="text-muted-foreground mt-1">Ciao {judge?.first_name}, seleziona la startup che vuoi votare</p>
        </div>

        <div className="space-y-3">
          {startups.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => handleSelectStartup(s.id)}
              className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                selectedStartup === s.id
                  ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
              data-testid={`vote-startup-${s.id}`}
            >
              <div className="flex items-center gap-4">
                {s.logo ? <img src={s.logo.startsWith('http') ? s.logo : `${process.env.REACT_APP_BACKEND_URL}${s.logo}`} alt="" className="w-14 h-14 rounded-lg object-contain bg-white/10 p-1.5" /> : <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center"><Rocket size={24} className="text-primary" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-lg">{s.name}</p>
                  {s.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{s.description}</p>}
                </div>
                {selectedStartup === s.id && <CheckCircle size={24} className="text-primary flex-shrink-0" />}
              </div>
            </button>
          ))}
        </div>

        <Button
          ref={confirmVoteBtnRef}
          onClick={handleVote}
          disabled={!selectedStartup || voteSubmitting}
          className="w-full"
          size="lg"
          data-testid="confirm-vote-btn"
        >
          {voteSubmitting ? <Loader2 size={18} className="mr-2 animate-spin" /> : <CheckCircle size={18} className="mr-2" />}
          Conferma Voto
        </Button>
      </div>
    </div>
  );
};
